import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import axios from 'axios';
import { 
  TrendingUp, 
  Clock, 
  Package, 
  AlertCircle, 
  Users,
  ChefHat,
  Calendar,
  RefreshCw,
  Zap,
  TrendingDown,
  Activity,
  Cloud,
  CloudRain,
  Sun,
  Bell,
  CheckCircle,
  XCircle,
  BarChart3,
  Thermometer,
  Wind,
  Droplets,
  Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../layout/PageContainer';




interface WeatherInfo {
  date: string;
  temperature: number;
  humidity: number;
  weather: string;
  weather_code: string;
  impact_factor: number;
}

interface IntegratedPrediction {
  predicted_customers: number;
  preparations: {
    [key: string]: number;
  };
  peak_hours: string[];
  special_notes: string;
  confidence_level: string;
  method: 'gemini_ai' | 'simple_model';
  weather?: WeatherInfo;
  weather_advice?: string;
}

interface PeakTimeAnalysis {
  peak_hours: Array<{
    time_slot: string;
    expected_customers: number;
    recommended_staff: number;
    type: string;
  }>;
  staff_schedule: {
    [key: string]: number;
  };
  efficiency_tips: string;
}

interface HourlyPrediction {
  hour: string;
  predicted_customers: number;
  predicted_sales: number;
  staff_recommendation: number;
  confidence: string;
}

interface InventoryOptimization {
  item_name: string;
  current_stock: number;
  unit: string;
  days_of_stock: number;
  recommendation: string;
  action: string | null;
  optimal_stock: number;
  reorder_quantity: number;
}

interface ApiUsage {
  daily_calls: number;
  daily_limit: number;
  remaining_calls: number;
  usage_percentage: string;
}

interface MenuPopularity {
  menu_name: string;
  menu_id: string;
  total_quantity: number;
  order_count: number;
  total_revenue: number;
  avg_price: number;
  sales_percentage: number;
}

interface PredictionHistory {
  id: number;
  prediction_date: string;
  prediction_type: string;
  prediction_data: any;
  confidence_level: string;
  accuracy_rate: number | null;
  created_at: string;
}

const AIPredictionEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const [integratedPrediction, setIntegratedPrediction] = useState<IntegratedPrediction | null>(null);
  const [peakTimeAnalysis, setPeakTimeAnalysis] = useState<PeakTimeAnalysis | null>(null);
  const [hourlyPredictions, setHourlyPredictions] = useState<HourlyPrediction[]>([]);
  const [inventoryOptimizations, setInventoryOptimizations] = useState<InventoryOptimization[]>([]);
  const [menuPopularity, setMenuPopularity] = useState<MenuPopularity[]>([]);
  const [predictionHistory, setPredictionHistory] = useState<PredictionHistory[]>([]);
  const [apiUsage, setApiUsage] = useState<ApiUsage | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'preparation' | 'peak' | 'hourly' | 'menu' | 'history'>('preparation');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // TODO: 将来的にfeature flagやDB設定でアンロック判定に切り替え
  const isUnlocked = false;

  useEffect(() => {
    if (!isUnlocked) return;
    fetchAllPredictions();
    fetchApiUsage();
    // 30分ごとに自動更新
    const interval = setInterval(() => {
      fetchAllPredictions();
    }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedDate, isUnlocked]);

  const fetchAllPredictions = async () => {
    setLoading(true);
    try {
      // 統合予測（天気情報付き）
      const integratedRes = await apiClient.get(
        `/ai-predictions/integrated-prediction?date=${selectedDate}`
      );
      setIntegratedPrediction(integratedRes.data);

      // ピークタイム分析
      const peakRes = await apiClient.get(
        `/ai-predictions/peak-times?date=${selectedDate}`
      );
      setPeakTimeAnalysis(peakRes.data);

      // 時間帯別予測
      const hourlyRes = await apiClient.get(
        `/ai-predictions/hourly-predictions?date=${selectedDate}`
      );
      setHourlyPredictions(hourlyRes.data);

      // 在庫最適化
      const inventoryRes = await apiClient.get(
        '/ai-predictions/inventory-optimization'
      );
      setInventoryOptimizations(inventoryRes.data);

      // メニュー人気度
      const menuRes = await apiClient.get(
        '/ai-predictions/menu-popularity?days=30'
      );
      setMenuPopularity(menuRes.data);

      // 予測履歴
      const historyRes = await apiClient.get(
        '/ai-predictions/prediction-history?limit=10'
      );
      setPredictionHistory(historyRes.data);

      setLastUpdate(new Date());
      showNotificationBriefly('予測データを更新しました');
    } catch (error) {
      console.error('予測データ取得エラー:', error);
      showNotificationBriefly('予測データの取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const res = await apiClient.get('/ai-predictions/api-usage');
      setApiUsage(res.data);
    } catch (error) {
      console.error('API使用状況取得エラー:', error);
    }
  };

  const showNotificationBriefly = (message: string, type: 'success' | 'error' = 'success') => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const getConfidenceColor = (level: string) => {
    switch (level) {
      case '高':
        return 'bg-green-100 text-green-800';
      case '中':
        return 'bg-yellow-100 text-yellow-800';
      case '低':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecommendationIcon = (recommendation: string) => {
    switch (recommendation) {
      case '在庫切れ':
      case '至急発注':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case '発注推奨':
        return <TrendingDown className="h-5 w-5 text-yellow-500" />;
      case '適正':
        return <Activity className="h-5 w-5 text-green-500" />;
      case '過剰在庫':
        return <Package className="h-5 w-5 text-purple-500" />;
      default:
        return null;
    }
  };

  const getWeatherIcon = (code: string) => {
    if (code?.includes('rain')) return <CloudRain className="h-6 w-6 text-blue-500" />;
    if (code?.includes('cloud')) return <Cloud className="h-6 w-6 text-gray-500" />;
    return <Sun className="h-6 w-6 text-yellow-500" />;
  };

  const getWeatherImpactColor = (factor: number) => {
    if (factor < 0.9) return 'text-red-600';
    if (factor > 1.1) return 'text-green-600';
    return 'text-gray-600';
  };

  if (!isUnlocked) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        {/* 注意バナー */}
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          borderRadius: 12,
          padding: '24px 28px',
          color: '#fff',
          marginBottom: 24,
          boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <AlertCircle style={{ width: 32, height: 32, flexShrink: 0, marginTop: 2 }} />
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px 0' }}>
                機能の一時停止について
              </h2>
              <p style={{ fontSize: 14, lineHeight: 1.8, margin: 0, opacity: 0.95 }}>
                本機能は一度デモ動作いたしましたが、確認したところ一部タブにおいて不具合が見つかりました。
                当初の実装予定範囲外であったため、エラー修正および本番運用に耐えうる品質での再構築には
                <strong> プレミアムオプション契約</strong> が必要となります。
                ご興味のある方は開発担当までお問い合わせください。
              </p>
            </div>
          </div>
        </div>

        {/* プレミアムオプション案内 */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0f766e 100%)',
          borderRadius: 16,
          padding: '48px 32px',
          color: '#fff',
          textAlign: 'center',
          marginBottom: 24,
          boxShadow: '0 20px 60px rgba(30, 58, 95, 0.3)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F916;</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px 0', letterSpacing: 1 }}>
            プレミアムオプション
          </h1>
          <h2 style={{ fontSize: 20, fontWeight: 400, margin: '0 0 12px 0', opacity: 0.9 }}>
            AI予測ダッシュボード
          </h2>
          <p style={{ fontSize: 14, opacity: 0.7, margin: '0 0 32px 0' }}>
            Gemini AI による高精度な需要予測で、仕込みロス削減・売上最大化を実現
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
            maxWidth: 960,
            margin: '0 auto 32px',
            textAlign: 'left'
          }}>
            {[
              { icon: '\u{1F372}', title: '仕込み量予測', desc: 'AIが過去の売上データ・天気・曜日を分析し、最適な仕込み量を自動算出。食材ロスを大幅削減' },
              { icon: '\u{23F0}', title: 'ピークタイム分析', desc: '時間帯別の来客予測で、スタッフ配置の最適化を実現。人件費の無駄を削減' },
              { icon: '\u{1F4C8}', title: '時間帯別売上予測', desc: '1時間単位の売上・来客予測で、準備のタイミングを逃さない' },
              { icon: '\u{1F4E6}', title: '在庫最適化', desc: '発注タイミングと適正在庫量をAIが自動判定。在庫切れ・過剰在庫を防止' },
              { icon: '\u{2B50}', title: 'メニュー人気度分析', desc: '売れ筋メニューをランキング表示。メニュー改定や仕入れ戦略の意思決定を支援' },
              { icon: '\u{26C5}', title: '天気連動予測', desc: '気象庁データと連携し、天候が売上に与える影響をリアルタイムで反映' },
              { icon: '\u{1F4CA}', title: '予測精度の可視化', desc: '過去の予測と実績を比較し、AIの予測精度を継続的に改善' },
              { icon: '\u{1F4B0}', title: '売上最大化', desc: '需要予測に基づく戦略的な仕込み・人員配置で、機会損失を最小化' },
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 12,
                padding: '16px 20px',
                backdropFilter: 'blur(10px)',
              }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{item.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{item.title}</div>
                <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>

          {/* 導入メリット */}
          <div style={{
            maxWidth: 700,
            margin: '0 auto 32px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 12,
            padding: '24px 28px',
            textAlign: 'left',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>
              &#x1F4A1; 導入で期待できる効果
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: 14 }}>
              {[
                '食材ロス 20〜30% 削減',
                '仕込み作業の効率化',
                '人件費の最適化',
                '在庫切れによる機会損失防止',
                'データに基づく経営判断',
                'スタッフ配置の最適化',
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#34d399', fontSize: 16 }}>&#x2714;</span>
                  <span style={{ opacity: 0.9 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 8,
            padding: '12px 32px',
            fontSize: 14,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
          }}>
            &#x1F6A7; 開発検討中 &#x2015; ご契約・お問い合わせは開発担当まで
          </div>
        </div>

        {/* ぼかしプレビュー */}
        <div style={{ position: 'relative' }}>
          <div style={{
            filter: 'blur(6px)',
            opacity: 0.4,
            pointerEvents: 'none',
            userSelect: 'none',
          }}>
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Zap className="h-7 w-7 text-yellow-500" />
                  AI予測ダッシュボード
                </h1>
              </div>
              {/* ダミー統計カード */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { label: '予測来客数', value: '42名', icon: <Users className="h-6 w-6 text-blue-500" /> },
                  { label: '予測売上', value: '¥128,500', icon: <TrendingUp className="h-6 w-6 text-green-500" /> },
                  { label: '信頼度', value: '高', icon: <Activity className="h-6 w-6 text-purple-500" /> },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {item.icon}
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-800">{item.value}</div>
                  </div>
                ))}
              </div>
              {/* ダミータブ */}
              <div className="flex gap-2 mb-4">
                {['仕込み予測', 'ピークタイム', '時間帯別', '在庫最適化', 'メニュー分析', '予測履歴'].map((tab, i) => (
                  <div key={i} className={`px-4 py-2 rounded-lg text-sm font-medium ${i === 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {tab}
                  </div>
                ))}
              </div>
              {/* ダミーテーブル */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="space-y-3">
                  {['ロースかつ: 15枚', 'ヒレかつ: 8枚', 'エビフライ: 20本', 'キャベツ: 5kg', 'ご飯: 3升'].map((item, i) => (
                    <div key={i} className="flex justify-between items-center bg-white p-3 rounded">
                      <span className="text-gray-700">{item}</span>
                      <div className="w-32 h-3 bg-blue-200 rounded-full">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${70 - i * 10}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ロックオーバーレイ */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 10,
          }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <AlertCircle style={{ width: 48, height: 48, color: '#9ca3af', opacity: 0.6 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center px-8 py-12 bg-white rounded-2xl shadow-2xl max-w-md">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <h2 className="mt-6 text-2xl font-bold text-gray-800">AI予測を生成中...</h2>
          <p className="mt-4 text-lg text-gray-600">
            Gemini AIが過去のデータから<br />
            予測を生成しています
          </p>
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800 font-medium">
              ⏱️ 予測の生成には1〜2分程度かかります
            </p>
            <p className="text-xs text-yellow-700 mt-2">
              初回アクセス時や、データ量が多い場合は<br />
              さらに時間がかかることがあります
            </p>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Activity className="h-4 w-4 animate-pulse" />
            <span>しばらくお待ちください</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 通知 */}
      {showNotification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${
          notificationMessage.includes('失敗') ? 'bg-red-500' : 'bg-green-500'
        } text-white flex items-center gap-2 transition-all`}>
          {notificationMessage.includes('失敗') ? (
            <XCircle className="h-5 w-5" />
          ) : (
            <CheckCircle className="h-5 w-5" />
          )}
          {notificationMessage}
        </div>
      )}

      {/* ヘッダー */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <Zap className="h-8 w-8 text-yellow-500" />
              AI予測ダッシュボード
            </h1>
            <p className="text-gray-600 mt-1">
              {integratedPrediction?.method === 'gemini_ai' 
                ? 'Gemini AIによる高精度予測' 
                : '統計モデルによる予測'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              最終更新: {lastUpdate.toLocaleTimeString('ja-JP')}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => navigate('/home')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Home className="h-4 w-4" />
                ホーム
              </button>
              <button
                onClick={fetchAllPredictions}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4" />
                再予測
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 天気情報 & API使用状況 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* 天気情報 */}
        {integratedPrediction?.weather && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getWeatherIcon(integratedPrediction.weather.weather_code)}
                <div>
                  <p className="text-sm font-medium text-gray-700">本日の天気</p>
                  <p className="text-lg font-semibold">{integratedPrediction.weather.weather}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex items-center gap-1">
                  <Thermometer className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{integratedPrediction.weather.temperature}°C</span>
                </div>
                <div className="flex items-center gap-1">
                  <Droplets className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{integratedPrediction.weather.humidity}%</span>
                </div>
              </div>
            </div>
            {integratedPrediction.weather_advice && (
              <p className={`text-sm mt-2 ${getWeatherImpactColor(integratedPrediction.weather.impact_factor)}`}>
                {integratedPrediction.weather_advice}
              </p>
            )}
          </div>
        )}

        {/* API使用状況 */}
        {apiUsage && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-500" />
                <span className="text-sm font-medium">API使用状況</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {apiUsage.daily_calls} / {apiUsage.daily_limit} 回
                </span>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      Number(apiUsage.usage_percentage) > 80 ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${apiUsage.usage_percentage}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{apiUsage.usage_percentage}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 日付選択 */}
      <div className="mb-6 flex items-center gap-4 bg-white rounded-lg shadow p-4">
        <Calendar className="h-5 w-5 text-gray-500" />
        <label className="text-sm font-medium text-gray-700">予測対象日:</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* タブナビゲーション */}
      <div className="mb-6 border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('preparation')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'preparation'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ChefHat className="h-4 w-4" />
            仕込み量提案
          </button>
          <button
            onClick={() => setActiveTab('peak')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'peak'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-4 w-4" />
            スタッフ配置
          </button>
          <button
            onClick={() => setActiveTab('hourly')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'hourly'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Clock className="h-4 w-4" />
            時間帯別予測
          </button>
          {/* 在庫最適化タブ - 非表示（2026-02-05: 在庫関連機能非表示のため） */}
          {/* <button
            onClick={() => setActiveTab('inventory')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'inventory'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="h-4 w-4" />
            在庫最適化
          </button> */}
          <button
            onClick={() => setActiveTab('menu')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'menu'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            メニュー分析
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bell className="h-4 w-4" />
            予測履歴
          </button>
        </nav>
      </div>

      {/* 仕込み量提案タブ */}
      {activeTab === 'preparation' && integratedPrediction && (
        <div className="space-y-6">
          {/* サマリーカード */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">予測来客数</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {integratedPrediction.predicted_customers}名
                  </p>
                  {integratedPrediction.weather?.impact_factor && (
                    <p className={`text-xs mt-1 ${getWeatherImpactColor(integratedPrediction.weather.impact_factor)}`}>
                      天候補正: {Math.round((integratedPrediction.weather.impact_factor - 1) * 100)}%
                    </p>
                  )}
                </div>
                <TrendingUp className="h-10 w-10 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">ピークタイム</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {integratedPrediction.peak_hours.join(', ')}
                  </p>
                </div>
                <Clock className="h-10 w-10 text-orange-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">予測信頼度</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getConfidenceColor(integratedPrediction.confidence_level)}`}>
                    {integratedPrediction.confidence_level}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {integratedPrediction.method === 'gemini_ai' ? 'AI予測' : '統計予測'}
                  </p>
                </div>
                <AlertCircle className="h-10 w-10 text-purple-500" />
              </div>
            </div>
          </div>

          {/* 推奨仕込み量 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ChefHat className="h-5 w-5" />
              本日の推奨仕込み量
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(integratedPrediction.preparations).map(([item, count]) => (
                <div key={item} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-gray-600 font-medium">{item}</p>
                  <p className="text-3xl font-bold text-blue-800">{count}</p>
                  <p className="text-xs text-gray-500">
                    {item.includes('キャベツ') ? '玉' : '枚'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* 特記事項 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">AIからのアドバイス</p>
                <p className="text-yellow-700 mt-1">{integratedPrediction.special_notes}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* スタッフ配置タブ */}
      {activeTab === 'peak' && peakTimeAnalysis && (
        <div className="space-y-6">
          {/* ピーク時間帯 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">ピーク時間帯分析</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {peakTimeAnalysis.peak_hours.map((peak, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold">{peak.type}</span>
                    <span className="text-sm text-gray-500">{peak.time_slot}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">予測客数</p>
                      <p className="text-2xl font-bold">{peak.expected_customers}名</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">推奨スタッフ</p>
                      <p className="text-2xl font-bold text-blue-600">{peak.recommended_staff}名</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* スタッフスケジュール */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">推奨スタッフ配置</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(peakTimeAnalysis.staff_schedule).map(([time, staff]) => (
                <div key={time} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <span className="text-sm font-medium">{time}</span>
                  <span className="text-lg font-bold text-blue-600">{staff}名</span>
                </div>
              ))}
            </div>
          </div>

          {/* 効率化のヒント */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Zap className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">効率化のヒント</p>
                <p className="text-green-700 mt-1">{peakTimeAnalysis.efficiency_tips}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 時間帯別予測タブ */}
      {activeTab === 'hourly' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  時間帯
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  予測来客数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  予測売上
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  推奨スタッフ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  信頼度
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hourlyPredictions.map((hour) => (
                <tr key={hour.hour}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {hour.hour}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {hour.predicted_customers}名
                      </span>
                      <div className="ml-3 w-20 bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 bg-blue-500 rounded-full"
                          style={{ width: `${Math.min(hour.predicted_customers * 3, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ¥{hour.predicted_sales?.toLocaleString() || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-bold text-blue-600">
                      {hour.staff_recommendation}名
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getConfidenceColor(hour.confidence)}`}>
                      {hour.confidence}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 在庫最適化タブ - 非表示（2026-02-05: 在庫関連機能非表示のため） */}
      {/* {activeTab === 'inventory' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  品目
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  現在庫
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  在庫日数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  推奨在庫
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  発注数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ステータス
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inventoryOptimizations.map((item) => (
                <tr key={item.item_name}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.item_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.current_stock} {item.unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.days_of_stock.toFixed(1)}日分
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.optimal_stock} {item.unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.reorder_quantity > 0 ? `${item.reorder_quantity} ${item.unit}` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getRecommendationIcon(item.recommendation)}
                      <div>
                        <span className={`text-sm font-medium ${
                          item.recommendation === '在庫切れ' || item.recommendation === '至急発注'
                            ? 'text-red-600'
                            : item.recommendation === '発注推奨'
                            ? 'text-yellow-600'
                            : item.recommendation === '適正'
                            ? 'text-green-600'
                            : 'text-purple-600'
                        }`}>
                          {item.recommendation}
                        </span>
                        {item.action && (
                          <p className="text-xs text-gray-500 mt-0.5">{item.action}</p>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )} */}

      {/* メニュー分析タブ */}
      {activeTab === 'menu' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">メニュー人気度ランキング（過去30日）</h3>
          </div>
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  順位
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  メニュー名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  販売数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  売上高
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  平均単価
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  売上占有率
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {menuPopularity.map((menu, index) => (
                <tr key={menu.menu_id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {menu.menu_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {menu.total_quantity}個
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ¥{menu.total_revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ¥{menu.avg_price.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {menu.sales_percentage}%
                      </span>
                      <div className="ml-3 w-20 bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 bg-green-500 rounded-full"
                          style={{ width: `${menu.sales_percentage}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 予測履歴タブ */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">予測履歴と精度</h3>
          </div>
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  予測日
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  予測タイプ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  信頼度
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  精度
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  作成日時
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {predictionHistory.map((history) => (
                <tr key={history.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(history.prediction_date).toLocaleDateString('ja-JP')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {history.prediction_type === 'sales_preparation' ? '売上・仕込み' : 
                     history.prediction_type === 'peak_time' ? 'ピークタイム' : 
                     history.prediction_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      getConfidenceColor(history.confidence_level)
                    }`}>
                      {history.confidence_level}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {history.accuracy_rate !== null ? (
                      <div className="flex items-center">
                        <span className={`font-medium ${
                          history.accuracy_rate >= 80 ? 'text-green-600' :
                          history.accuracy_rate >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {history.accuracy_rate.toFixed(1)}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">未測定</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(history.created_at).toLocaleString('ja-JP')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AIPredictionEnhanced;